import React, { useState, useMemo } from 'react';
import { reachOf, impressionsFor } from '../utils/reportMetrics';
import ReactDOM from 'react-dom';
import { getFollowerLabel } from '../utils/platformHelpers';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import PlatformIconShared from './PlatformIcon';
import EditContentMetricsModal from './EditContentMetricsModal';

// API base URL for thumbnail resolution
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper to resolve thumbnail URLs (local cached thumbnails start with /api/)
const resolveThumbnailUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
};

const CampaignCreatorCard = ({ creator, contents, gameName, gameId, onContentDeleted, onContentUpdated, onCreatorRemoved, onCreatorUpdated, campaignId }) => {
  const [expanded, setExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [editCost, setEditCost] = useState('');
  const [editCurrency, setEditCurrency] = useState('GBP');
  const [savingCost, setSavingCost] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [removingCreator, setRemovingCreator] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [showPausePopover, setShowPausePopover] = useState(false);
  const [pauseStartDate, setPauseStartDate] = useState('');
  const [pausing, setPausing] = useState(false);

  // Coverage pause state (organic): an open window (no end) means currently paused.
  const pauseWindows = creator.pauseWindows || [];
  const openWindow = pauseWindows.find(w => w && !w.end);
  const isPaused = !!openWindow;

  const handlePauseCoverage = async (e) => {
    e.stopPropagation();
    setPausing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/creators/${creator.id}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(pauseStartDate ? { startDate: new Date(pauseStartDate).toISOString() } : {}),
      });
      if (!res.ok) throw new Error('pause failed');
      setShowPausePopover(false);
      setPauseStartDate('');
      onCreatorUpdated && onCreatorUpdated();
    } catch (err) {
      console.error('Error pausing coverage:', err);
      alert('Failed to pause coverage');
    } finally { setPausing(false); }
  };

  const handleResumeCoverage = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Resume tracking ${creator.name}'s coverage from now? Their paused period stays excluded.`)) return;
    setPausing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/creators/${creator.id}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('resume failed');
      onCreatorUpdated && onCreatorUpdated();
    } catch (err) {
      console.error('Error resuming coverage:', err);
      alert('Failed to resume coverage');
    } finally { setPausing(false); }
  };
  // const [refreshingStatsId, setRefreshingStatsId] = useState(null);

  // Calculate stats for the SPECIFIC GAME only
  const gameStats = useMemo(() => {
    const normalizedGameName = gameName?.toLowerCase().trim();

    let totalViews = 0;
    let totalGameDuration = 0; // Minutes for THIS game only
    let totalCCV = 0;
    let peakCCV = 0;
    let combinedPeakCCV = 0;
    let totalStreamWatchHours = 0; // Sum of all stream watch hours
    let streamCount = 0;
    let vodCount = 0;
    let videoCount = 0;
    // YouTube/TikTok-specific metrics
    let totalLikes = 0;
    let totalComments = 0;
    let totalShares = 0;
    let videoViewsForEngagement = 0;

    contents.forEach(content => {
      const isStreamContent = content.contentType === 'stream' || content.contentType === 'vod' || content.platform === 'twitch' || content.platform === 'kick';
      // Reach: a stream counts by its VOD views, everything else by views — matches
      // the campaign/report so a Twitch creator isn't shown as 0 views/impressions.
      totalViews += isStreamContent ? (content.vodViews || 0) : (content.views || 0);

      // YouTube/TikTok-specific: accumulate likes, comments, shares
      if (content.platform === 'youtube' || content.platform === 'tiktok') {
        totalLikes += content.likes || 0;
        totalComments += content.comments || 0;
        totalShares += content.shares || 0;
        if (content.views) videoViewsForEngagement += content.views;
      }

      // Check if content has segments for THIS game
      const gameSegments = content.segments?.filter(seg => {
        const segGameName = seg.gameName?.toLowerCase().trim();
        const segGameTitle = seg.game?.title?.toLowerCase().trim();

        return seg.gameId === gameId ||
               seg.game?.id === gameId ||
               segGameName === normalizedGameName ||
               segGameTitle === normalizedGameName;
      }) || [];

      if (gameSegments.length > 0) {
        // Only count duration for streams/VODs, not YouTube videos
        if (isStreamContent) {
          const segmentDuration = gameSegments.reduce((sum, seg) => sum + (seg.durationMinutes || 0), 0) || content.streamDuration;
          totalGameDuration += segmentDuration;
        }

        // Use segment-level stats when available
        const segmentAvgCCV = gameSegments.reduce((sum, seg) => sum + (seg.avgCcv || 0), 0) / gameSegments.length || content.streamAvgCcv || content.avgCcv || 0;
        const segmentPeakCCV = Math.max(...gameSegments.map(seg => seg.peakCcv || 0)) || content.streamPeakCcv || content.peakCcv || 0;
        const segmentWatchHours = gameSegments.reduce((sum, seg) => sum + (seg.watchHours || 0), 0);

        totalCCV += segmentAvgCCV;
        peakCCV = Math.max(peakCCV, segmentPeakCCV);
        combinedPeakCCV += segmentPeakCCV;
        totalStreamWatchHours += segmentWatchHours || content.streamWatchHours || 0;
      } else {
        // No segments - use content-level stats
        if (content.streamWatchHours) {
          totalStreamWatchHours += content.streamWatchHours;
        }
        if (isStreamContent) {
          totalGameDuration += content.streamDuration || content.duration || 0;
        }
        totalCCV += content.streamAvgCcv || content.avgCcv || 0;
        const contentPeak = content.streamPeakCcv || content.peakCcv || 0;
        peakCCV = Math.max(peakCCV, contentPeak);
        combinedPeakCCV += contentPeak;
      }

      // Count by type
      if (content.contentType === 'stream' || content.contentType === 'vod') {
        if (content.contentType === 'stream') streamCount++;
        else vodCount++;
      } else {
        videoCount++;
      }
    });

    // Calculate watch hours - use sum of stream watch hours if available, otherwise calculate from avgCCV × duration
    const avgCCV = contents.length > 0 ? totalCCV / contents.length : 0;
    const watchHours = totalStreamWatchHours > 0 ? totalStreamWatchHours : Math.round((avgCCV * (totalGameDuration / 60)) || 0);

    // Calculate YouTube/TikTok engagement rate
    const engagementRate = videoViewsForEngagement > 0
      ? ((totalLikes + totalComments) / videoViewsForEngagement) * 100
      : 0;

    // Debug logging
    if (contents.length > 0 && contents[0].streamAvgCcv) {
      console.log('[GameStats Debug]', {
        gameName,
        contentsCount: contents.length,
        totalStreamWatchHours,
        totalCCV,
        avgCCV: Math.round(avgCCV),
        peakCCV,
        totalGameDuration,
        firstContentStreamStats: {
          streamAvgCcv: contents[0].streamAvgCcv,
          streamPeakCcv: contents[0].streamPeakCcv,
          streamWatchHours: contents[0].streamWatchHours
        }
      });
    }

    // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is estimated
    // at 5x rather than 10x, so multiplying the summed view total is wrong.
    const impressions = contents.reduce((sum, c) => sum + impressionsFor(c, reachOf(c)), 0);

    // Track which types of content exist
    const hasStreamContent = streamCount > 0 || vodCount > 0 || contents.some(c => c.platform === 'twitch' || c.platform === 'kick');
    const hasVideoContent = videoCount > 0 || contents.some(c => c.platform === 'youtube' || c.platform === 'tiktok');
    const hasTiktok = contents.some(c => c.platform === 'tiktok');

    return {
      totalViews,
      impressions,
      totalGameDuration,
      avgCCV: Math.round(avgCCV),
      peakCCV,
      combinedCCV: Math.round(totalCCV),
      combinedPeakCCV: Math.round(combinedPeakCCV),
      watchHours,
      streamCount,
      vodCount,
      videoCount,
      // YouTube/TikTok-specific
      totalLikes,
      totalComments,
      totalShares,
      engagementRate,
      // Content type flags
      hasStreamContent,
      hasVideoContent,
      hasTiktok
    };
  }, [contents, gameId, gameName]);

  // Sort by date (newest first) and deduplicate
  // Prefer content with segments, then by newest date
  const sortedContents = useMemo(() => {
    // Deduplicate: Group by vodId/videoId, keep one with most segments
    const contentMap = new Map();
    
    contents.forEach(content => {
      // Same dedup key as the backend / campaign list: a bare channel URL (e.g.
      // twitch.tv/afrodited13) is shared by every one of a creator's streams, so
      // never collapse on it — fall back to the unique content id. Otherwise two
      // distinct daily streams with no specific VOD link would merge into one.
      const nUrl = content.contentUrl
        ? content.contentUrl.toLowerCase().replace('://www.', '://').replace(/\/+$/, '')
        : null;
      let urlVideoId = null;
      if (nUrl) {
        const tw = nUrl.match(/\/videos\/(\d+)/);
        const tk = nUrl.match(/\/video\/(\d+)/);
        const yt = nUrl.match(/(?:[?&]v=|\/shorts\/|youtu\.be\/)([a-z0-9_-]+)/i);
        urlVideoId = (tw && `tw_${tw[1]}`) || (tk && `tk_${tk[1]}`) || (yt && `yt_${yt[1]}`) || null;
      }
      const isSpecificVodUrl = nUrl && /\/(videos|video|watch|v|shorts)\/[\w-]+/.test(nUrl);
      const key = content.vodId || urlVideoId || content.videoId || (isSpecificVodUrl ? nUrl : null) || content.id;
      const existing = contentMap.get(key);
      
      // Keep the one with more segments, or the newer one
      if (!existing) {
        contentMap.set(key, content);
      } else {
        const existingSegmentCount = existing.segments?.length || 0;
        const newSegmentCount = content.segments?.length || 0;
        
        if (newSegmentCount > existingSegmentCount ||
            (newSegmentCount === existingSegmentCount && 
             new Date(content.postDate) > new Date(existing.postDate))) {
          contentMap.set(key, content);
        }
      }
    });
    
    return Array.from(contentMap.values()).sort((a, b) => 
      new Date(b.postDate) - new Date(a.postDate)
    );
  }, [contents]);

  // Show everything the backend already attached to this campaign for this
  // creator (via CampaignInfluencer.Content or the ContentCampaign join).
  // The previous gameId/segment/title filter was a defensive second pass that
  // produced false negatives when a VOD was legitimately on the campaign but
  // lacked a gameId, had segments only for other games (multi-game streams),
  // and the title didn't contain the game name verbatim — matching the
  // report editor's behaviour avoids this.
  // Sort is user-selectable; 'missing-first' preserves the old automatic
  // behaviour (items lacking views/date float to the top for fixing).
  const [sortMode, setSortMode] = useState('missing-first');
  const filteredContents = useMemo(() => {
    const hasMissingData = (c) =>
      c.views == null || c.views === 0 ||
      !c.postDate || new Date(c.postDate).getFullYear() < 2000;
    const byDateDesc = (a, b) => new Date(b.postDate || 0) - new Date(a.postDate || 0);
    const list = [...sortedContents]; // already newest-first
    if (sortMode === 'missing-first') {
      return list.sort((a, b) =>
        (hasMissingData(b) ? 1 : 0) - (hasMissingData(a) ? 1 : 0)
      );
    }
    if (sortMode === 'date-asc') return list.sort((a, b) => byDateDesc(b, a));
    return list.sort(byDateDesc); // 'date-desc'
  }, [sortedContents, sortMode]);

  const platforms = [...new Set(contents.map(c => c.platform))];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const handleRemoveCreator = async (e) => {
    e.stopPropagation(); // Prevent card expansion

    if (!window.confirm(`Remove ${creator.name} from this campaign? This will not delete the creator or their content, just remove them from this campaign.`)) {
      return;
    }

    setRemovingCreator(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/creators/${creator.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove creator from campaign');
      }

      // Notify parent to refresh
      if (onCreatorRemoved) {
        onCreatorRemoved(creator.id);
      }
    } catch (error) {
      console.error('Error removing creator:', error);
      alert('Failed to remove creator from campaign');
    } finally {
      setRemovingCreator(false);
    }
  };

  // Per-content: just flip Paid <-> Organic (cost lives on the creator budget).
  const toggleContentPaid = async (e, content) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/content/${content.id}/payment`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPaid: !content.isPaid }),
      });
      if (res.ok && onContentUpdated) onContentUpdated();
    } catch (err) { console.error('Error toggling paid:', err); }
  };

  const handleSaveCost = async () => {
    setSavingCost(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/${campaignId}/creators/${creator.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isSponsored: true,
            agreedRate: editCost ? parseFloat(editCost) : null,
            currency: editCurrency
          })
        }
      );
      if (response.ok) {
        setShowCostModal(false);
        if (onCreatorUpdated) onCreatorUpdated();
      }
    } catch (err) {
      console.error('Error saving cost:', err);
    } finally {
      setSavingCost(false);
    }
  };

  // Use shared PlatformIcon component (YouTube icon meets 20dp minimum + accessibility)
  const PlatformIcon = ({ platform }) => <PlatformIconShared platform={platform} size="sm" />;

  const getContentTypeIcon = (type) => {
    const icons = {
      vod: '📺',
      video: '▶️',
      stream: '🔴',
      clip: '✂️',
      short: '📱'
    };
    return icons[type] || '📄';
  };

  // Get segment data for a specific content item
  const getGameSegmentData = (content) => {
    const normalizedGameName = gameName?.toLowerCase().trim();
    
    // Filter segments: prioritize title match over ID match
    const gameSegments = content.segments?.filter(seg => {
      const segGameName = seg.gameName?.toLowerCase().trim();
      const segGameTitle = seg.game?.title?.toLowerCase().trim();
      
      // Prioritize title match (most important)
      if (segGameName === normalizedGameName || segGameTitle === normalizedGameName) {
        return true;
      }
      // Fallback to ID match
      return seg.gameId === gameId || seg.game?.id === gameId;
    }) || [];

    if (gameSegments.length > 0) {
      // Prefer segment data (game-specific duration) over streamDuration (full stream)
      const duration = gameSegments.reduce((sum, seg) => sum + (seg.durationMinutes || 0), 0) || content.streamDuration;
      const avgCcv = Math.round(gameSegments.reduce((sum, seg) => sum + (seg.avgCcv || 0), 0) / gameSegments.length) || content.streamAvgCcv || content.avgCcv;
      const peakCcv = Math.max(...gameSegments.map(seg => seg.peakCcv || 0)) || content.streamPeakCcv || content.peakCcv;
      const watchHours = gameSegments.reduce((sum, seg) => sum + (seg.watchHours || 0), 0);

      return { duration, avgCcv, peakCcv, watchHours: watchHours || null, hasSegments: true };
    }

    return {
      duration: content.streamDuration || content.duration || 0,
      avgCcv: content.streamAvgCcv || content.avgCcv,
      peakCcv: content.streamPeakCcv || content.peakCcv,
      watchHours: null,
      hasSegments: false
    };
  };

  const handleDeleteContent = async (contentId, contentTitle) => {
    // First click: show confirmation
    if (deleteConfirmId !== contentId) {
      setDeleteConfirmId(contentId);
      // Auto-hide confirmation after 5 seconds
      setTimeout(() => {
        setDeleteConfirmId(null);
      }, 5000);
      return;
    }

    // Second click: proceed with deletion
    setDeletingId(contentId);
    setDeleteConfirmId(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/content/${contentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      // Backend returns { message: '...' } on success, { error: '...' } on failure
      if (response.ok && !data.error) {
        // Notify parent component to refresh
        if (onContentDeleted) {
          onContentDeleted(contentId);
        } else {
          // Fallback to reload if no callback provided
          window.location.reload();
        }
      } else {
        console.error('Delete failed:', data.error || data.message);
        alert(`Failed to delete content: ${data.error || data.message}`);
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete content. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-[#241d33] border border-white rounded-xl overflow-hidden hover:border-white transition-colors group">
      {/* Creator Row Card */}
      <div className="p-3 flex items-center gap-3">
        {/* Creator identity - left */}
        <div className="flex items-center gap-2.5 flex-shrink-0 w-48">
          <img
            src={creator.profileImageUrl || creator.avatarUrl || creator.profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.name || 'Unknown')}&background=6366f1&color=fff&size=128&bold=true`}
            alt={creator.name}
            className="w-9 h-9 rounded-full object-cover border border-white flex-shrink-0"
            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.name || 'Unknown')}&background=6366f1&color=fff&size=128&bold=true`; }}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <h3 className="font-semibold text-white text-sm truncate">{creator.name}</h3>
              {creator.country && <span className="text-[10px] text-white font-medium flex-shrink-0">{creator.country}</span>}
              {creator.isSponsored && creator.agreedRate > 0 ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditCost(creator.agreedRate || ''); setEditCurrency(creator.currency || 'GBP'); setShowCostModal(true); }}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-sc-pink/15 text-sc-pink font-semibold hover:bg-sc-pink/25 flex-shrink-0"
                  title="Edit paid budget"
                >
                  {({ GBP: '£', USD: '$', EUR: '€' }[creator.currency] || '£')}{Number(creator.agreedRate).toLocaleString()}
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditCost(''); setEditCurrency(creator.currency || 'GBP'); setShowCostModal(true); }}
                  className="text-[10px] px-1.5 py-0.5 rounded text-white hover:text-white hover:bg-white/10 flex-shrink-0"
                  title="Set paid budget for this creator"
                >
                  + Budget
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {platforms.map(platform => (<PlatformIcon key={platform} platform={platform} />))}
              {creator.followerCount > 0 && <span className="text-[11px] text-white">{formatNumber(creator.followerCount)}</span>}
            </div>
          </div>
        </div>

        {/* Metrics pills - middle, flowing */}
        <div className="flex-1 flex flex-wrap gap-1 items-center">
          {[
            { label: 'Content', value: filteredContents.length },
            { label: 'Views', value: formatNumber(gameStats.totalViews), tooltip: 'views' },
            ...(gameStats.hasStreamContent ? [
              { label: 'Combined CCV', value: formatNumber(gameStats.combinedCCV) },
              { label: 'Combined Peak CCV', value: formatNumber(gameStats.combinedPeakCCV) },
              { label: 'Combined Stream Duration', value: formatDuration(gameStats.totalGameDuration) },
              { label: 'Watch Hours', value: formatNumber(gameStats.watchHours), tooltip: 'watchHours' },
              { label: 'Retention', value: gameStats.avgCCV > 0 && gameStats.peakCCV > 0 ? `${((gameStats.avgCCV / gameStats.peakCCV) * 100).toFixed(0)}%` : '—' },
            ] : []),
            ...(gameStats.hasVideoContent ? [
              { label: 'Likes', value: formatNumber(gameStats.totalLikes) },
              { label: 'Engagement', value: gameStats.engagementRate > 0 ? `${gameStats.engagementRate.toFixed(1)}%` : '—', tooltip: 'engagement' },
            ] : []),
            { label: 'Impressions', value: formatNumber(gameStats.impressions), tooltip: 'impressions' },
          ].map((m, i) => (
            <span key={i} className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1 bg-gradient-to-r from-pink-500/15 to-purple-500/15 border border-sc-pink">
              <span className="text-[11px] text-white">{m.label}</span>
              {m.tooltip && <YouTubeEstimateTooltip metric={m.tooltip} />}
              <span className="text-[11px] font-semibold text-white">{m.value}</span>
            </span>
          ))}
        </div>

        {/* Right side - date + actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {contents.length > 0 && (
            <span className="text-[10px] text-white whitespace-nowrap">
              {formatDate(sortedContents[sortedContents.length - 1]?.postDate)} → {formatDate(sortedContents[0]?.postDate)}
            </span>
          )}
          <button
            onClick={() => setExpanded(true)}
            className="text-[10px] px-2.5 py-1 rounded-full bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 font-medium transition-colors whitespace-nowrap"
          >
            View Content
          </button>
          {/* Coverage pause (organic): backdatable, excludes their coverage for the paused window */}
          {isPaused ? (
            <button
              onClick={handleResumeCoverage}
              disabled={pausing}
              className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/25 text-[10px] font-medium flex-shrink-0 flex items-center gap-1 disabled:opacity-50"
              title={`Coverage paused${openWindow?.start ? ' since ' + new Date(openWindow.start).toLocaleDateString('en-GB') : ''} — click to resume`}
            >
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
              {pausing ? '…' : 'Paused'}
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setShowPausePopover(true); }}
              disabled={pausing}
              className="p-1 hover:bg-white/10 rounded-full text-white hover:text-white disabled:opacity-50 flex-shrink-0"
              title="Pause coverage"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
            </button>
          )}
          <button onClick={handleRemoveCreator} disabled={removingCreator} className="p-1 hover:bg-red-500/10 rounded-full text-red-400 hover:text-red-300 disabled:opacity-50 flex-shrink-0" title="Remove from campaign">
            {removingCreator ? <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /> : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          </button>
        </div>
      </div>

      {/* Pause coverage modal (portal — never clipped by the card) */}
      {showPausePopover && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => { setShowPausePopover(false); setPauseStartDate(''); }}>
          <div className="bg-[#1a1525] border border-white rounded-xl w-full max-w-sm mx-4 shadow-2xl shadow-black/50 p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-white mb-1">Pause coverage — {creator.name}</h3>
            <p className="text-xs text-white mb-4">Leave the date blank to pause from now, or backdate it to drop earlier coverage from the campaign, portal and stats. Nothing is deleted — resuming brings it back.</p>
            <label className="block text-xs text-white mb-1">Pause from</label>
            <input
              type="date"
              value={pauseStartDate}
              onChange={(e) => setPauseStartDate(e.target.value)}
              className="w-full px-3 py-2 mb-4 bg-white/5 border border-white rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-sc-pink"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowPausePopover(false); setPauseStartDate(''); }} className="text-xs text-white hover:text-white px-3 py-1.5">Cancel</button>
              <button onClick={handlePauseCoverage} disabled={pausing} className="text-xs font-medium bg-sc-pink/20 text-sc-pink border border-sc-pink/30 rounded-md px-3 py-1.5 hover:bg-sc-pink/30 disabled:opacity-50">{pausing ? 'Pausing…' : 'Pause coverage'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Content Modal Popup */}
      {expanded && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1525] border border-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col mx-4 shadow-2xl">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <img src={creator.profileImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.name || 'Unknown')}&background=6366f1&color=fff&size=64&bold=true`} alt="" className="w-8 h-8 rounded-full object-cover" />
                <div>
                  <h3 className="text-sm font-semibold text-white">{creator.name}</h3>
                  <span className="text-[10px] text-white">{filteredContents.length} content pieces</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="bg-white/[0.06] border border-white rounded-md text-xs text-white px-2 py-1 focus:outline-none focus:border-pink-500/40 [&>option]:bg-[#1a1525]"
                  title="Sort content"
                >
                  <option value="missing-first">Missing metrics first</option>
                  <option value="date-desc">Date (newest first)</option>
                  <option value="date-asc">Date (oldest first)</option>
                </select>
                <button onClick={() => setExpanded(false)} className="p-1 text-white hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Modal content - scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredContents.map((content, index) => {
                const segmentData = getGameSegmentData(content);
                return (
                  <div key={content.id || index} className="p-3 bg-white/[0.06] rounded-lg border border-white">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 relative w-24 h-14">
                        {content.thumbnail || content.thumbnailUrl ? (
                          <img src={resolveThumbnailUrl((content.thumbnail || content.thumbnailUrl).replace('%{width}', '96').replace('%{height}', '56'))} alt="" className="w-full h-full object-cover rounded" />
                        ) : (
                          <div className="w-full h-full bg-white/10 rounded flex items-center justify-center text-lg">{getContentTypeIcon(content.contentType)}</div>
                        )}
                        <span className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/75 text-white text-[10px] rounded">{formatDuration(segmentData.duration)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white line-clamp-2">{content.title || `${content.platform} ${content.contentType}`}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-white">
                          <PlatformIcon platform={content.platform} />
                          <span>{content.contentType}</span>
                          <span>•</span>
                          <span>{formatDate(content.postDate)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={(e) => toggleContentPaid(e, content)}
                          className={`px-2 py-1 text-xs rounded font-medium ${content.isPaid ? 'text-sc-pink bg-sc-pink/10 hover:bg-sc-pink/20' : 'text-white hover:bg-white/10'}`}
                          title="Toggle paid / organic"
                        >
                          {content.isPaid ? 'Paid' : 'Organic'}
                        </button>
                        {content.contentUrl && (() => {
                          let viewUrl = content.contentUrl;
                          if (content.platform === 'twitch' && content.contentUrl.includes('/videos/') && content.postDate) {
                            const gameSegs = (content.segments || []).filter(seg => seg.gameId === gameId || seg.game?.id === gameId).sort((a, b) => (a.segmentOrder || 0) - (b.segmentOrder || 0));
                            if (gameSegs.length > 0 && gameSegs[0].startTime) {
                              const offsetSecs = Math.max(0, Math.floor((new Date(gameSegs[0].startTime) - new Date(content.postDate)) / 1000));
                              if (offsetSecs > 0) { const h = Math.floor(offsetSecs / 3600); const m = Math.floor((offsetSecs % 3600) / 60); const s = offsetSecs % 60; viewUrl = `${content.contentUrl.split('?')[0]}?t=${h}h${m}m${s}s`; }
                            }
                          }
                          return <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs text-sc-pink hover:bg-sc-pink/10 rounded" onClick={(e) => e.stopPropagation()}>View</a>;
                        })()}
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const segData = getGameSegmentData(content);
                          if (segData.hasSegments) {
                            const gameSegs = (content.segments || []).filter(seg => {
                              const n = gameName?.toLowerCase().trim();
                              const sn = seg.gameName?.toLowerCase().trim();
                              const st = seg.game?.title?.toLowerCase().trim();
                              return sn === n || st === n || seg.gameId === gameId || seg.game?.id === gameId;
                            });
                            setEditingContent({
                              ...content,
                              streamDuration: segData.duration,
                              streamAvgCcv: segData.avgCcv,
                              streamPeakCcv: segData.peakCcv,
                              _gameSegmentIds: gameSegs.map(s => s.id),
                            });
                          } else {
                            setEditingContent(content);
                          }
                        }} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Edit metrics">✏️</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteContent(content.id, content.title); }} disabled={deletingId === content.id} className={`px-2 py-1 text-xs rounded ${deleteConfirmId === content.id ? 'bg-red-600 text-white' : 'text-red-400 hover:bg-red-500/10'}`}>
                          {deletingId === content.id ? '...' : deleteConfirmId === content.id ? 'Confirm?' : '×'}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pt-2 border-t border-white/10 text-xs text-white">
                      {(content.platform === 'youtube' || content.platform === 'tiktok' || content.platform === 'instagram' || content.platform === 'twitter') ? (
                        <>
                          <span><strong>{formatNumber(content.views || 0)}</strong> views</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><strong>{formatNumber(impressionsFor(content, reachOf(content)))}</strong> impressions <YouTubeEstimateTooltip metric="impressions" /></span>
                          <span><strong>{formatNumber(content.likes || 0)}</strong> likes</span>
                          <span><strong>{formatNumber(content.comments || 0)}</strong> comments</span>
                          {(content.platform === 'tiktok' || content.platform === 'twitter') && <span><strong>{formatNumber(content.shares || 0)}</strong> {content.platform === 'twitter' ? 'retweets' : 'shares'}</span>}
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <strong>{content.views && content.likes ? `${(((content.likes + (content.comments || 0)) / content.views) * 100).toFixed(2)}%` : 'N/A'}</strong> engagement
                            <YouTubeEstimateTooltip metric="engagement" />
                          </span>
                          {(content.contentType === 'stream' || content.contentType === 'vod') && (
                            <>
                              <span><strong>{formatNumber(content.streamAvgCcv || content.avgCcv || 0)}</strong> avg CCV</span>
                              <span><strong>{formatNumber(content.streamPeakCcv || content.peakCcv || 0)}</strong> peak CCV</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><strong>{formatNumber(content.streamWatchHours || content.watchHours || 0)}</strong> watch hrs <YouTubeEstimateTooltip metric="watchHours" /></span>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <span><strong>{formatDuration(segmentData.duration)}</strong> time</span>
                          <span><strong>{formatNumber(segmentData.hasSegments ? (segmentData.avgCcv || 0) : (content.streamAvgCcv || content.avgCcv || 0))}</strong> avg CCV</span>
                          <span><strong>{formatNumber(segmentData.hasSegments ? (segmentData.peakCcv || 0) : (content.streamPeakCcv || content.peakCcv || 0))}</strong> peak CCV</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><strong>{formatNumber((segmentData.hasSegments && segmentData.watchHours) ? segmentData.watchHours : (content.streamWatchHours || content.watchHours || 0))}</strong> watch hrs <YouTubeEstimateTooltip metric="watchHours" /></span>
                          <span><strong>{formatNumber(content.vodViews || content.views || 0)}</strong> VOD views</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><strong>{formatNumber(impressionsFor(content, reachOf(content)))}</strong> impressions <YouTubeEstimateTooltip metric="impressions" /></span>
                          <span><strong>{(() => { const avg = segmentData.hasSegments ? segmentData.avgCcv : (content.streamAvgCcv || content.avgCcv); const peak = segmentData.hasSegments ? segmentData.peakCcv : (content.streamPeakCcv || content.peakCcv); return avg && peak && peak > 0 ? `${((avg / peak) * 100).toFixed(0)}%` : 'N/A'; })()}</strong> retention</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Content Metrics Modal */}
      <EditContentMetricsModal
        isOpen={!!editingContent}
        onClose={() => setEditingContent(null)}
        onSuccess={() => {
          setEditingContent(null);
          if (onContentUpdated) onContentUpdated();
        }}
        content={editingContent}
      />

      {/* Sponsored Cost Modal - rendered via portal to escape overflow-hidden */}
      {showCostModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCostModal(false)}>
          <div className="bg-gray-900 border border-white rounded-lg p-5 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-white mb-3">
              Set Sponsored Cost
            </h3>
            <p className="text-sm text-white mb-3">{creator.name}</p>
            <div className="flex items-center gap-2 mb-4">
              <select
                value={editCurrency}
                onChange={(e) => setEditCurrency(e.target.value)}
                className="px-2 py-2 border border-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white"
              >
                <option value="GBP">£ GBP</option>
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
              </select>
              <input
                type="number"
                value={editCost}
                onChange={(e) => setEditCost(e.target.value)}
                placeholder="0.00"
                className="flex-1 px-3 py-2 border border-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCost();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCostModal(false)}
                className="px-3 py-1.5 text-sm border border-white rounded-lg hover:bg-white/5 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCost}
                disabled={savingCost}
                className="px-3 py-1.5 text-sm btn-pink disabled:opacity-50"
              >
                {savingCost ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default CampaignCreatorCard;
